import { useTranslation } from "react-i18next";
import {
  MAX_CYCLE_WEEKS,
  MIN_CYCLE_WEEKS,
  programRoundLabel,
  RoundType,
  type RoundType as RoundTypeT,
} from "@heraion/shared";

import { SheetMenu } from "../primitives/SheetMenu";
import type { UiTheme } from "../theme";
import { CopyOntoTray } from "./CopyOntoTray";
import { useRoundTypeWords } from "../primitives/RoundTypeBadge";
import type { V3DayActions } from "./useV3DayActions";

/** Which round the menu is on, and whether it shows the list, the «Copy
 *  onto…» targets or the types. Null: closed. */
export type RoundMenuState = { week: number; mode: "menu" | "copy" | "type" } | null;

// The round menu (#336, brief §7.2 «Menu del giro», mock :459-462), opened
// by a tap on a W head of the heatmap: duplicate right after, copy onto
// another round, its type (normal, deload, test, S2), move earlier or later,
// remove. «Copy onto…» and «Type» open the same dialog as the day menu's.
export function RoundMenu({
  theme,
  state,
  setState,
  cycleWeeks,
  typeOf,
  days,
}: {
  theme: UiTheme;
  state: RoundMenuState;
  setState: (s: RoundMenuState) => void;
  cycleWeeks: number;
  /** Round `w`'s type (roundTypeAt). */
  typeOf: (w: number) => RoundTypeT;
  days: V3DayActions;
}) {
  const { t } = useTranslation();
  const words = useRoundTypeWords();
  const w = state?.week ?? 0;
  const n = w + 1;
  const round = programRoundLabel(n);
  const type = typeOf(w);
  const close = () => setState(null);
  return (
    <>
      <SheetMenu
        theme={theme}
        visible={state?.mode === "menu"}
        onClose={close}
        title={programRoundLabel(n, type, words)}
        closeLabel={t("program.cancel", { defaultValue: "Cancel" })}
        items={[
          {
            key: "dup",
            icon: "copy",
            label: t("editorV3.duplicateRound", { defaultValue: "Duplicate round" }),
            onPress: () => days.duplicateRound(w),
            disabled: cycleWeeks >= MAX_CYCLE_WEEKS,
          },
          {
            key: "copy",
            icon: "arrow-right",
            label: t("editorV3.copyOnto", { defaultValue: "Copy onto…" }),
            onPress: () => setState({ week: w, mode: "copy" }),
            disabled: cycleWeeks < 2,
          },
          {
            key: "type",
            icon: "tag",
            label: t("editorV3.roundTypeMenu", { defaultValue: "Round type…" }),
            onPress: () => setState({ week: w, mode: "type" }),
          },
          {
            key: "left",
            icon: "chevron-left",
            label: t("editorV3.moveRoundEarlier", { defaultValue: "Move earlier" }),
            onPress: () => days.moveRound(w, -1),
            disabled: w === 0,
          },
          {
            key: "right",
            icon: "chevron-right",
            label: t("editorV3.moveRoundLater", { defaultValue: "Move later" }),
            onPress: () => days.moveRound(w, 1),
            disabled: w >= cycleWeeks - 1,
          },
          {
            key: "remove",
            icon: "trash",
            label: t("editorV3.removeRound", { n, defaultValue: "Remove W{{n}}" }),
            onPress: () => days.removeRoundAsked(w),
            destructive: true,
            disabled: cycleWeeks <= MIN_CYCLE_WEEKS,
          },
        ]}
      />
      {state?.mode === "copy" ? (
        <CopyOntoTray
          theme={theme}
          question={t("editorV3.copyRoundQuestion", { round, defaultValue: "Copy {{round}} onto which round?" })}
          options={Array.from({ length: cycleWeeks }, (_, i) => ({
            key: String(i),
            label: programRoundLabel(i + 1),
          })).filter((o) => o.key !== String(w))}
          onChoose={(key) => {
            close();
            days.copyRoundOnto(w, Number(key));
          }}
          footnote={t("editorV3.copyRoundFootnote", {
            defaultValue: "The round you pick loses what it has now, in every day.",
          })}
          onCancel={close}
        />
      ) : null}
      {state?.mode === "type" ? (
        <CopyOntoTray
          theme={theme}
          question={t("editorV3.roundTypeQuestion", { round, defaultValue: "What kind of round is {{round}}?" })}
          options={RoundType.options.map((k) => ({ key: k, label: words[k], selected: k === type }))}
          onChoose={(key) => {
            close();
            const picked = RoundType.parse(key);
            days.setRoundType(w, picked, words[picked]);
          }}
          footnote={t("editorV3.roundTypeFootnoteRules", {
            defaultValue: "The type holds for the whole program. Each exercise gets its effect from its rule.",
          })}
          onCancel={close}
        />
      ) : null}
    </>
  );
}
